import type { LegalDocumentFamily } from '@alamo/api';

export const LEGAL_DOCUMENTS_PATH = '/admin/documentos-juridicos';

export const LEGAL_DOCUMENTS_PERMISSIONS = [
  { resource: 'legal_terms', action: 'read' },
  { resource: 'contract_templates', action: 'manage' },
  { resource: 'consultant_contract_templates', action: 'manage' },
  { resource: 'quotation_general_conditions', action: 'manage' },
];

export const LEGAL_DOCUMENT_FAMILIES: LegalDocumentFamily[] = [
  'associate',
  'consultant',
  'acceptance',
  'refund',
  'quotation',
];

type PermissionCheck = (resource: string, action: string) => boolean;

export function canManageLegalDocuments(family: LegalDocumentFamily, check: PermissionCheck) {
  switch (family) {
    case 'acceptance':
      return check('legal_terms', 'manage');
    case 'consultant':
      return check('consultant_contract_templates', 'manage');
    case 'associate':
      return check('contract_templates', 'manage');
    case 'refund':
      return check('contract_templates', 'manage') && check('refund_term_templates', 'read');
    case 'quotation':
      return check('quotation_general_conditions', 'manage');
  }
}

export function legalDocumentManagementPath(family: LegalDocumentFamily): string {
  switch (family) {
    case 'associate':
      return `${LEGAL_DOCUMENTS_PATH}/associados?scope=plan`;
    case 'refund':
      return `${LEGAL_DOCUMENTS_PATH}/estornos?scope=refund`;
    case 'consultant':
      return `${LEGAL_DOCUMENTS_PATH}/consultores`;
    case 'acceptance':
      return `${LEGAL_DOCUMENTS_PATH}/aceites`;
    case 'quotation':
      return `${LEGAL_DOCUMENTS_PATH}/condicoes-cotacao`;
  }
}

export function legalDocumentCatalogPath(
  family?: LegalDocumentFamily,
  currentParams?: URLSearchParams,
): string {
  const params = new URLSearchParams({ view: 'catalog' });
  for (const key of ['search', 'limit', 'visibility']) {
    const value = currentParams?.get(key);
    if (value) params.set(key, value);
  }
  if (family) params.set('family', family);
  return `${LEGAL_DOCUMENTS_PATH}${params.size ? `?${params}` : ''}`;
}

export function legalDocumentEditPath(
  family: LegalDocumentFamily,
  id: string,
  search?: string,
): string {
  switch (family) {
    case 'associate':
    case 'refund':
      return `${LEGAL_DOCUMENTS_PATH}/modelos/${encodeURIComponent(id)}`;
    case 'consultant':
      return `${LEGAL_DOCUMENTS_PATH}/consultores/${encodeURIComponent(id)}`;
    case 'acceptance':
    case 'quotation':
      return `${legalDocumentManagementPath(family)}${search ? `?search=${encodeURIComponent(search)}` : ''}`;
  }
}

export function isLegalDocumentsPath(pathname: string): boolean {
  return (
    pathname === LEGAL_DOCUMENTS_PATH ||
    pathname.startsWith(`${LEGAL_DOCUMENTS_PATH}/`) ||
    pathname === '/admin/legal-terms' ||
    pathname === '/admin/condicoes-gerais-cotacao' ||
    pathname === '/admin/contratos/templates' ||
    pathname.startsWith('/admin/contratos/templates/') ||
    pathname === '/admin/contratos/templates-consultor' ||
    pathname.startsWith('/admin/contratos/templates-consultor/')
  );
}
